import { CssBaseline, ThemeProvider } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { themeSettings } from "theme";
import Layout from "scenes/layout";
import Dashboard from "scenes/dashboard";
import Products from "scenes/products";
import Customers from "scenes/customers";
import Transactions from "scenes/transactions";
import Geography from "scenes/geography";
import Overview from "scenes/overview";
import Daily from "scenes/daily";
import Monthly from "scenes/monthly";
import Breakdown from "scenes/breakdown";
import Admin from "scenes/admin";
import Performance from "scenes/performance";
import LoginPage from "scenes/loginPage";
import Users from "scenes/timeManagement/users";
import ShiftsPage from "scenes/timeManagement/shifts";
import ShiftCalendar from "scenes/timeManagement/shifts11";
import TimeRequest from "scenes/timeManagement/timeRequest";
import Availability from "scenes/timeManagement/availability";
import Footprints from "scenes/timeManagement/footprints";
import ClockManagement from "scenes/timeManagement/clocks";
import WeeklyHours from "scenes/timeManagement/weeklyHours";
import LaborCosting from "scenes/timeManagement/laborCosting";
import OvertimeExceptions from "scenes/timeManagement/overtimeExceptions";
import ProductivityAnalytics from "scenes/timeManagement/productivityAnalytics";
import PersonalInfo from "scenes/portfolio/personalInfo";
import Skills from "scenes/portfolio/skills";
import Projects from "scenes/portfolio/projects";
import Experiences from "scenes/portfolio/experiences";
import Education from "scenes/portfolio/education";
import AlertDisplay from "components/AlertDisplay";

/**
 * A component to protect routes that require authentication.
 * If the user is authenticated (token exists), it renders the child routes (e.g., Layout).
 * Otherwise, it redirects the user to the login page.
 */
const ProtectedRoute = ({ children }) => {
  const token = useSelector((state) => state.global.token);
  if (!token) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  const mode = useSelector((state) => state.global.mode);
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  
  return (
    <div className="app">
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AlertDisplay />
          <Routes>
            <Route path="/" element={<LoginPage />} />
            {/* All routes inside this element are now protected */}
            <Route 
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/products" element={<Products />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/transactions" element={<Transactions />} />
              <Route path="/geography" element={<Geography />} />
              <Route path="/overview" element={<Overview />} />
              <Route path="/daily" element={<Daily />} />
              <Route path="/monthly" element={<Monthly />} />
              <Route path="/breakdown" element={<Breakdown />} />
              <Route path="/admin/administrators" element={<Admin />} />
              <Route path="/admin/footprints" element={<Footprints />} />
              <Route path="/performance" element={<Performance />} />
              <Route path="/time management/users" element={<Users />} />
              <Route path="/time management/shifts" element={<ShiftsPage />} />
              <Route path="/time management/shifts11" element={<ShiftCalendar />} />
              <Route path="/time management/clocks" element={<ClockManagement />} />
              <Route path="/time management/availability" element={<Availability />} />
              <Route path="/time management/footprints" element={<Footprints />} />
              <Route path="/time management/time off requests" element={<TimeRequest />} />
              <Route path="/time management/weekly hours" element={<WeeklyHours />} />
              <Route path="/time management/labor costing" element={<LaborCosting />} />
              <Route path="/time management/overtime exceptions" element={<OvertimeExceptions />} />
              <Route path="/time management/productivity analytics" element={<ProductivityAnalytics />} />
              <Route path="/portfolio/personal Info" element={<PersonalInfo />} />
              <Route path="/portfolio/skills" element={<Skills />} />
              <Route path="/portfolio/projects" element={<Projects />} />
              <Route path="/portfolio/experiences" element={<Experiences />} />
              <Route path="/portfolio/education" element={<Education />} />
            </Route>
          </Routes>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
